// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Edit one scalar system_settings value
// 2. Separation of Concerns - Parsing/UI only; parent handles API
// 3. Modular Architecture - Used for voice_processing string/number/object
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Modal editor for non-array system settings (string, number, object JSON).
 * QA Context / glossary use array + SettingEditor instead.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
} from '@mui/material';

function formatInitialText(setting) {
  if (!setting) return '';
  const v = setting.setting_value;
  if (v === null || v === undefined) return '';
  if (setting.value_type === 'object') {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function coerceScalar(raw, valueType) {
  const trimmed = raw.trim();
  if (valueType === 'string') {
    return raw;
  }
  if (valueType === 'number') {
    if (trimmed === '') {
      throw new Error('Number is required');
    }
    if (/^-?\d+$/.test(trimmed)) {
      return parseInt(trimmed, 10);
    }
    const n = parseFloat(trimmed);
    if (Number.isNaN(n)) {
      throw new Error('Invalid number');
    }
    return n;
  }
  if (valueType === 'object') {
    if (trimmed === '') {
      throw new Error('JSON is required');
    }
    return JSON.parse(trimmed);
  }
  return raw;
}

const ScalarSettingEditor = ({ isOpen, setting, onSave, onCancel }) => {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && setting) {
      setText(formatInitialText(setting));
      setError(null);
    }
  }, [isOpen, setting]);

  if (!setting) {
    return null;
  }

  const { value_type: valueType, display_name: displayName, description } = setting;
  const isLongString =
    valueType === 'string' &&
    (String(setting.setting_key || '').includes('prompt') ||
      String(displayName || '').toLowerCase().includes('prompt') ||
      String(text).length > 120);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const parsed = coerceScalar(text, valueType);
      await onSave(parsed);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Edit value — {displayName}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label={valueType === 'object' ? 'JSON value' : 'Value'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline={valueType === 'string' ? isLongString || text.includes('\n') : valueType === 'object'}
            minRows={valueType === 'object' ? 12 : isLongString ? 10 : valueType === 'string' ? 3 : 1}
            inputProps={
              valueType === 'number'
                ? { inputMode: 'decimal' }
                : undefined
            }
            helperText={
              valueType === 'number'
                ? 'Integer or decimal (e.g. 5, 0.5)'
                : valueType === 'object'
                  ? 'Valid JSON object or array'
                  : undefined
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ScalarSettingEditor;
